import type {AnswerId, Id} from 'src/types/chatbot';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import classify from 'src/utils/classify';
import makeClassNameComponent, {
  type ClassNameComponent,
} from 'src/utils/makeClassNameComponent';
import {useHoverTip} from 'src/components/lib/mouse-tip/mouse-tip';

import {
  FormLabelMedium,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Truncate} from '@spaced-out/ui-design-system/lib/components/Truncate';
import {Input as GenesisInput} from '@spaced-out/ui-design-system/lib/components/Input';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';
import CaretDownIcon from 'src/images/icons/caret-down.svg?noAttrs';

import WarningIcon from 'src/images/icons/warning-icon.svg';

import css from './common.css';
import nCss from 'src/components/conversation-builder/node-editor-v2/node-editor-v2.css';

// @ts-expect-error - TS2322 - Type 'AbstractComponent<ComponentProps<C>, unknown>' is not assignable to type 'ClassNameComponent'.
export const TitleText: ClassNameComponent = makeClassNameComponent(
  css.titleText,
);
export const DescriptorText: ClassNameComponent<'span'> =
  makeClassNameComponent(css.descriptorText, 'span');

export function HoverError({
  className,
  error,
  errors,
}: {
  className?: string | null | undefined;
  error?: string;
  errors?: Array<string>;
}) {
  const hoverProps = useHoverTip({
    content: errors != null ? errors.join(',') : error ?? ' ',
    fixedTo: 'top',
  });

  return (
    // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | undefined'.
    <div {...hoverProps} className={className}>
      <WarningIcon />
    </div>
  );
}

export const getOptOutMessage = (
  t: (arg1: string, arg2: string) => string,
): string =>
  t(
    'Reply_STOP_to_quit_HELP_for_info_MsgDataRatesMayApply',
    'Reply STOP to quit, HELP for info. Msg&DataRatesMayApply',
  );

export function OptOutMessage({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  const {t} = useLabelTranslation();
  const optOutMessage = getOptOutMessage(t);
  const hoverProps = useHoverTip({
    content:
      'This sentence cannot be deleted and will always show up at the end of the message.',
    fixedTo: 'right',
  });
  return (
    // @ts-expect-error - TS2322 - Type '{ children: string; className: string; ref: { current: HTMLElement | null | undefined; }; onMouseEnter: (arg1: MouseEvent<HTMLElement, MouseEvent>) => void; ... 275 more ...; onTransitionStartCapture?: TransitionEventHandler<...> | undefined; }' is not assignable to type 'DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>'.
    <div
      {...props}
      {...hoverProps}
      className={classify(css.optOutMessage, className)}
    >
      {optOutMessage}
    </div>
  );
}

type MixVariableValue =
  | string
  | boolean
  | {
      name: string;
      node_id?: Id;
    };
type MixVariableToken = {
  id?: AnswerId;
  value: MixVariableValue;
  label?: string;
  type: string;
  disabled?: boolean;
  writeback_field_value?: string;
};

export const MixVariableInput = ({
  dropdownOptionGroups,
  values,
  handleSelectToken,
  handleRemoveToken,
  allowArbitraryValues,
  disabled = false,
  // @ts-expect-error - TS2741 - Property 'dropdown' is missing in type '{}' but required in type '{ dropdown: string; }'.
  classNames = {},
}: {
  dropdownOptionGroups: {
    groupTitle: string;
    groupType: string;
    options: {
      value: string;
      label: string;
      entityRelationshipPath?: string[];
    }[];
  }[];
  values: MixVariableToken[];
  handleSelectToken: (arg1: string, arg2: string, arg3: string) => void;
  handleRemoveToken: (arg1: MixVariableToken) => void;
  allowArbitraryValues: boolean;
  disabled: boolean;
  classNames?: {
    dropdown: string;
  };
}) => {
  const {t, makeKey} = useLabelTranslation();
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredDropdownOptionGroups = React.useMemo(
    () =>
      dropdownOptionGroups.map((groupData) => ({
        ...groupData,
        options: groupData.options.filter(
          (option) =>
            String(option?.label ? option.label : '')
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            String(option?.value ? option.value : '')
              .toLowerCase()
              .includes(searchTerm.toLowerCase()),
        ),
      })),
    [searchTerm],
  );

  return (
    <ClickAway>
      {({isOpen, onOpen, triggerRef, boundaryRef}) => (
        // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
        <div className={nCss.jobMatchCriteriaContainer} ref={triggerRef}>
          <div
            className={classify(nCss.jobMatchCriteriaInputContainer, {
              [nCss.jobMatchCriteriaInputContainerWithDropdown]: isOpen,
            })}
            onClick={disabled ? undefined : onOpen}
          >
            <div className={nCss.jobMatchCriteriaSelected}>
              <Truncate line={1} showFullTextOnHover>
                {values.map(({label}) => label).join(', ')}
              </Truncate>
            </div>
            {!disabled && (
              <div className={nCss.chevron}>
                {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                <CaretDownIcon className={classify(isOpen && nCss.chevronUp)} />
              </div>
            )}
          </div>
          {isOpen && (
            <div
              className={classify(
                nCss.jobMatchCriteriaDropdown,
                classNames.dropdown,
              )}
              ref={boundaryRef}
            >
              <GenesisInput
                classNames={{box: nCss.jobMatchCriteriaInput}}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                iconLeftName="search"
                autoFocus
              />

              <div className={nCss.mixedVariableDropdownChip}>
                {values.map((val) => (
                  <Chip
                    onDismiss={() => handleRemoveToken(val)}
                    semantic="primary"
                    dismissable
                    key={val.toString()}
                  >
                    {}
                    {val?.label ? String(val.label) : ''}
                  </Chip>
                ))}
              </div>
              {filteredDropdownOptionGroups.map(
                ({groupTitle, groupType, options}) => {
                  if (!options.length) {
                    return null;
                  }
                  return (
                    <>
                      <FormLabelSmall color="tertiary">
                        {t(makeKey(groupTitle ?? ''), groupTitle ?? '')}
                      </FormLabelSmall>
                      {options.map((option) => {
                        if (!option.label || !option.value) {
                          return null; //to appease flow
                        }
                        const {label, value} = option;
                        const alreadySelected = values.find(
                          (val) => val.label === label,
                        );

                        return (
                          <div
                            className={classify(
                              nCss.mixedVariableDropdownOption,
                              {
                                [nCss.mixedVariableDropdownOptionSelected]:
                                  alreadySelected,
                              },
                            )}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!alreadySelected) {
                                setSearchTerm('');
                                handleSelectToken(label, value, groupType);
                              }
                            }}
                          >
                            <FormLabelMedium>
                              <Truncate line={1} showFullTextOnHover>
                                {label ? String(label) : ''}
                              </Truncate>
                            </FormLabelMedium>
                            {groupType === 'ats_variables' && (
                              <FormLabelSmall color="tertiary">
                                {}
                                {option.entityRelationshipPath
                                  ?.slice(1)
                                  .reverse()
                                  .join('/')}
                              </FormLabelSmall>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                },
              )}
              {allowArbitraryValues && searchTerm && (
                <div
                  className={nCss.mixedVariableDropdownOption}
                  onClick={(e) => {
                    e.preventDefault();
                    setSearchTerm('');
                    handleSelectToken(searchTerm, searchTerm, 'default_values');
                  }}
                >
                  <FormLabelMedium>{`${t(
                    'Create',
                    'Create',
                  )}: ${searchTerm}`}</FormLabelMedium>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </ClickAway>
  );
};
